import { shortestAngleDiff } from "../types";

export interface RosieInit {
  x: number;
  y: number;
  heading: number;
  accent: string;
  name: string;
  isPlayer?: boolean;
  scale?: number;
  trailLength?: number;
}

export class Rosie {
  x: number;
  y: number;
  heading: number;
  speed = 170;
  turnRate = 3.2;
  accent: string;
  name: string;
  isPlayer: boolean;

  desiredHeading: number;

  trail: { x: number; y: number }[] = [];
  trailLength: number;

  alive = true;
  score = 0;

  /** Base collision radius. Multiply by `scale` for effective hit area. */
  radius = 12;

  /** Visual + collision multiplier. Player scales with score; bots get tiered scales on spawn. */
  scale: number;

  /** Wall-clock ms during which head-vs-trail collisions are skipped (Robot Mode). */
  invincibleUntil = 0;

  /** Player-only — how many other Rosies this one has killed in the current run. */
  kills = 0;

  constructor(init: RosieInit) {
    this.x = init.x;
    this.y = init.y;
    this.heading = init.heading;
    this.desiredHeading = init.heading;
    this.accent = init.accent;
    this.name = init.name;
    this.isPlayer = init.isPlayer ?? false;
    this.scale = init.scale ?? 1.0;
    this.trailLength = init.trailLength ?? 5;
  }

  /** Effective collision radius after applying scale. */
  get effRadius(): number {
    return this.radius * this.scale;
  }

  /** Effective trash-bag radius for this Rosie's trail. */
  get trailBagRadius(): number {
    return 7 * this.scale;
  }

  isInvincible(now: number): boolean {
    return now < this.invincibleUntil;
  }

  update(dt: number): void {
    if (!this.alive) return;

    const diff = shortestAngleDiff(this.heading, this.desiredHeading);
    const maxTurn = this.turnRate * dt;
    if (diff > maxTurn) this.heading += maxTurn;
    else if (diff < -maxTurn) this.heading -= maxTurn;
    else this.heading = this.desiredHeading;

    this.x += Math.cos(this.heading) * this.speed * dt;
    this.y += Math.sin(this.heading) * this.speed * dt;

    this.trail.unshift({ x: this.x, y: this.y });
    const maxCrumbs = Math.max(40, this.trailLength * 6);
    if (this.trail.length > maxCrumbs) this.trail.length = maxCrumbs;
  }
}
